import { createContext } from 'preact'
import { useCallback, useState, useEffect, useRef, useContext } from 'preact/hooks'

const initValue = {
  isLoading: false,
  results: [],
  options: new Map(),
  params: {},
  refine: () => {},
  clear: () => {}
}
const SearchspringContext = createContext(initValue)

export function useSearchspringFetcher(
  fetcher,
  initialiser = {
    options: {},
    runOnMount: true
  }
) {
  const { options: _options = {}, onFetch, runOnMount = true } = initialiser
  const isMounted = useRef(runOnMount)
  const [isLoading, setIsLoading] = useState(false)
  const [options, setOptions] = useState({ map: new Map(Object.entries(_options)) })
  const [results, setResults] = useState({})
  const params = fetcher?.params
  const refine = useCallback(
    (key, value) => {
      if (!value) return
      if (value === options.map.get(key)) return
      setOptions((prev) => {
        const next = new Map(prev.map)
        next.set(key, value)
        return { map: next }
      })
    },
    [options, setOptions]
  )

  const clear = useCallback(
    (key) => {
      if (!key) return
      setOptions((prev) => {
        const next = new Map(prev.map)
        next.delete(key)
        return { map: next }
      })
    },
    [options, setOptions]
  )

  useEffect(() => {
    function handleResultUpdate() {
      if (!isMounted.current) {
        isMounted.current = true
        return
      }
      setIsLoading(true)
      handleFetch()
    }

    const controller = new AbortController()
    handleResultUpdate()

    async function handleFetch() {
      try {
        fetcher.updateParams(Object.fromEntries(options.map.entries()))
        const data = await fetcher.fetch(controller)
        if (onFetch) {
          const normalisedResults = await onFetch(data, options.map)
          setResults(normalisedResults)
        } else {
          setResults(data)
        }
        setIsLoading(false)
      } catch (error) {
        if (controller.signal.aborted) return
        console.error(error)
        setIsLoading(false)
      }
    }
    return () => {
      controller.abort('Outdated Searchspring payload, aborting current request')
    }
  }, [options])

  return {
    isLoading,
    results,
    options: options.map,
    params,
    setOptions,
    refine,
    clear
  }
}

export function SearchspringProvider({ fetcher, onFetch, children, options, runOnMount }) {
  const value = useSearchspringFetcher(fetcher, {
    onFetch,
    options,
    runOnMount
  })

  return <SearchspringContext.Provider value={value}>{children}</SearchspringContext.Provider>
}

export function useSearchspringContext() {
  return useContext(SearchspringContext)
}

export function generateSearchspringProvider(FetcherClass, onFetch) {
  return function ({ children, category, runOnMount, ...options }) {
    const fetcherRef = useRef(new FetcherClass(category))
    return (
      <SearchspringProvider fetcher={fetcherRef.current} onFetch={onFetch} runOnMount={runOnMount} options={options}>
        {children}
      </SearchspringProvider>
    )
  }
}
